import { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import Modal from '@mui/material/Modal'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import {
  useParams,
  useNavigate,
  useSearchParams,
  useSubmit,
  useRouteLoaderData,
  useActionData,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  adminParticipantDetailsLoader,
  adminOptionsParticipantsLoader,
  adminEventDetailsLoader,
} from '../../Admin.loader'
import {
  getOption,
  useLocationDataOptions,
  useParticipantOptionStatusOptions,
} from '../../../../utils'
import { LoaderData, OptionType, OptionsParticipants } from '../../../../types'
import { putOptionParticipation } from '../../Admin.action'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  width: { xs: '100%', sm: '90%' },
  maxWidth: '50rem',
  height: { xs: '100%', sm: '80%' },
  overflow: 'auto',
}

const Title = styled('h2')(() => ({
  marginBottom: 0,
}))
const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
  flex: '1 0 auto',
}))

export default function EditParticipantOptions() {
  const { t } = useTranslation('admin')
  const submit = useSubmit()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const actionData = useActionData() as
    | LoaderData<ReturnType<typeof putOptionParticipation>>
    | undefined
  const { currentParticipation, optionId, eventId, participantId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const dataParticipant = useRouteLoaderData('participantsDetails') as LoaderData<
    ReturnType<typeof adminParticipantDetailsLoader>
  >
  const dataOptionsParticipants = useRouteLoaderData('optionsDetails') as LoaderData<
    ReturnType<typeof adminOptionsParticipantsLoader>
  >
  const data =
    optionId !== undefined
      ? dataOptionsParticipants?.participants?.results
      : dataParticipant?.participant_options

  const { options } = useRouteLoaderData('adminEventDetails') as LoaderData<
    ReturnType<typeof adminEventDetailsLoader>
  >

  const locationDataOptions = useLocationDataOptions()
  const participantOptionStatusOptions = useParticipantOptionStatusOptions(t)

  const [selectedRegistration, setSelectedRegistration] = useState(
    data?.find(el => el.id === Number(currentParticipation))
  )
  useEffect(() => {
    setSelectedRegistration(data?.find(el => el.id === Number(currentParticipation)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentParticipation])

  const [selectedParticipant, setSelectedParticipant] = useState(
    dataParticipant ?? selectedRegistration
  )

  const [option, setOption] = useState(
    options?.find(
      el =>
        el.id ===
        (!Number.isNaN(Number(optionId)) ? Number(optionId) : selectedRegistration?.option.id)
    )
  )
  useEffect(() => {
    setOption(
      options?.find(
        el =>
          el.id ===
          (!Number.isNaN(Number(optionId)) ? Number(optionId) : selectedRegistration?.option.id)
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionId, selectedRegistration])

  const optionType = option?.type

  useEffect(() => {
    if (actionData && 'data' in actionData) {
      if (optionId !== undefined) {
        navigate(`/admin/details/${eventId}/options/${optionId}?${searchParams.toString()}`)
      } else {
        navigate(
          `/admin/details/${eventId}/participants/${participantId}?${searchParams.toString()}`
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionData])

  const handleClose = () => {
    if (optionId !== undefined) {
      navigate(`/admin/details/${eventId}/options/${optionId}?${searchParams.toString()}`)
    } else {
      navigate(`/admin/details/${eventId}/participants/${participantId}?${searchParams.toString()}`)
    }
  }

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.target as HTMLFormElement)
    const parsedData = {
      status:
        getOption(participantOptionStatusOptions, formData.get('status') as string)?.value ?? null,
      present: (formData.get('present') as string) === 'on',
      hotel:
        option?.type === OptionType.SubEventOption
          ? (getOption(locationDataOptions, formData.get('hotel') as string)?.value ?? null)
          : null,
      value:
        option?.type === OptionType.NumberOption || option?.type === OptionType.TextOption
          ? (formData.get('value') as string)
          : '',
      optionId: option?.id ?? -1,
      participantId:
        participantId !== undefined
          ? Number(participantId)
          : ((selectedParticipant as OptionsParticipants<true>)?.participant_id ?? -1),
    }

    // TODO use zod validation and schema
    submit(parsedData, {
      method: 'POST',
      encType: 'application/json',
      action:
        optionId !== undefined
          ? `/admin/details/${eventId}/options/${optionId}/details/${currentParticipation}?${searchParams.toString()}`
          : `/admin/details/${eventId}/participants/${participantId}/option/${currentParticipation}?${searchParams.toString()}`,
    })
  }

  const participantOptions =
    currentParticipation === 'new' ? (dataOptionsParticipants?.participants?.results ?? []) : []

  return (
    <Modal open={true} onClose={handleClose}>
      <Stack sx={style}>
        <Form onSubmit={onSubmitForm}>
          <Stack>
            <Title>
              {currentParticipation === 'new'
                ? t('Ajouter une incription complémentaire')
                : t('Modifier une incription complémentaire')}
            </Title>
            <Divider />
          </Stack>
          <Stack gap="1rem" flex="1 0" margin="2rem 0">
            <Autocomplete
              disablePortal
              id="participant-autocomplete"
              disabled={
                currentParticipation !== 'new' ||
                (currentParticipation === 'new' && participantId !== undefined)
              }
              options={participantOptions}
              value={selectedParticipant}
              onChange={(_, value) => setSelectedParticipant(value)}
              getOptionLabel={el => `${el.first_name} ${el.last_name}`}
              fullWidth
              disableClearable
              renderInput={params => (
                <TextField
                  {...params}
                  label={t('Participant')}
                  name="participant"
                  // value={option?.name}
                  variant={
                    currentParticipation !== 'new' ||
                    (currentParticipation === 'new' && participantId !== undefined)
                      ? 'filled'
                      : 'outlined'
                  }
                />
              )}
            />
            <Autocomplete
              disablePortal
              id="options-autocomplete"
              disabled={
                currentParticipation !== 'new' ||
                (currentParticipation === 'new' && optionId !== undefined)
              }
              options={options ?? []}
              value={option}
              onChange={(_, value) => setOption(value)}
              getOptionLabel={el => el.name}
              fullWidth
              disableClearable
              renderInput={params => (
                <TextField
                  {...params}
                  label={t('Option')}
                  name="options"
                  value={option?.name}
                  variant={
                    currentParticipation !== 'new' ||
                    (currentParticipation === 'new' && optionId !== undefined)
                      ? 'filled'
                      : 'outlined'
                  }
                />
              )}
            />
            <Autocomplete
              disablePortal
              id="status-autocomplete"
              options={participantOptionStatusOptions}
              fullWidth
              disableClearable
              defaultValue={
                getOption(
                  participantOptionStatusOptions,
                  undefined,
                  selectedRegistration?.status
                ) ?? undefined
              }
              renderInput={params => (
                <TextField {...params} label={t("Statut d'inscription")} name="status" />
              )}
            />
            {optionType === OptionType.SubEventOption && (
              <Autocomplete
                disablePortal
                id="hotel-autocomplete"
                options={locationDataOptions}
                fullWidth
                defaultValue={
                  getOption(locationDataOptions, undefined, selectedRegistration?.hotel) ??
                  undefined
                }
                renderInput={params => <TextField {...params} label={t('Hôtel')} name="hotel" />}
              />
            )}
            {(optionType === OptionType.NumberOption || optionType === OptionType.TextOption) && (
              <TextField
                label={t('Valeur')}
                name="value"
                defaultValue={selectedRegistration?.value}
              />
            )}
            <FormControlLabel
              defaultChecked={!!selectedRegistration?.present}
              name="present"
              control={<Checkbox defaultChecked={!!selectedRegistration?.present} />}
              label={t('Présent')}
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between" paddingBottom="1.5rem">
            <Button
              onClick={e => {
                e.preventDefault()
                handleClose()
              }}
            >
              {t('Annuler')}
            </Button>
            <LoadingButton variant="contained" type="submit">
              {currentParticipation === 'new' ? t('Ajouter') : t('Modifier')}
            </LoadingButton>
          </Stack>
        </Form>
      </Stack>
    </Modal>
  )
}
